import { ButtonGroup, LinkButton, RaisedButton } from "lib/miolo";
import { FORM_ERROR } from "final-form";
import { useField, useForm } from "react-final-form-hooks";
import restartForm from "lib/utils/form/restartForm";
import type { ISelfieQuery } from "../../../lib/queries/__generated__/Selfie.generated";
import checkCanSubmit from "../../../lib/utils/form/checkCanSubmit";
import validateFormValuesAgainstSchema from "../../../lib/utils/form/validateFormValuesAgainstSchema";
import Avatar from "../../core/Avatar";
import * as palette from "../../palette";
import InlineErrorSpan from "../../shared/form/InlineErrorSpan";
import CommentInputTextArea from "../CommentInputTextArea";
import commentInputFormDataShape from "./shapes/commentInputFormDataShape";
import translateServerErrorIntoFriendlyMessage from "./translateServerErrorIntoFriendlyMessage";

interface IReplyCommentInputFormProps {
  viewer: NonNullable<ISelfieQuery["selfie"]>;
  bodyAsPlainText?: string;
  hideAvatar?: boolean;
  autoFocus?: boolean;
  hintAboutSupportMessagesBeingDeleted?: boolean;
  onCancel?: () => void;
  onSubmit: (bodyAsPlainText: string) => Promise<any>;
}

export default function ReplyCommentInputForm({
  viewer,
  bodyAsPlainText = "",
  hideAvatar = false,
  autoFocus = false,
  hintAboutSupportMessagesBeingDeleted = true,
  onCancel,
  onSubmit,
}: IReplyCommentInputFormProps) {
  const { form, handleSubmit, submitting, submitError, ...props } = useForm<{
    bodyAsPlainText: string;
  }>({
    onSubmit: async ({ bodyAsPlainText }, form) => {
      try {
        await onSubmit(bodyAsPlainText);

        setTimeout(() => {
          restartForm(form);
        }, 1);
      } catch (err) {
        if (err instanceof Error) {
          return {
            [FORM_ERROR]: translateServerErrorIntoFriendlyMessage(err),
          };
        }
        return {
          [FORM_ERROR]: "Erro desconhecido",
        };
      }

      return null;
    },
    validate: validateFormValuesAgainstSchema(commentInputFormDataShape),
    initialValues: {
      bodyAsPlainText,
    },
  });

  const bodyAsPlainTextField = useField("bodyAsPlainText", form);
  const canSubmit = submitting ? false : checkCanSubmit(props);

  return (
    <div
      css={{
        display: "grid",
        gridColumnGap: 16,
        gridTemplateColumns: hideAvatar ? "1fr" : "42px 1fr",
      }}
    >
      {!hideAvatar && (
        <Avatar
          src={viewer.pictureUrl}
          hashValue={viewer._id}
          alt={viewer.fullName}
          size={42}
          css={{
            WebkitBackfaceVisibility: "hidden",
          }}
        />
      )}

      <form
        onSubmit={handleSubmit}
        css={{
          display: "grid",
          gridRowGap: 20,
        }}
      >
        <CommentInputTextArea
          {...bodyAsPlainTextField.input}
          autoFocus={autoFocus}
          hintAboutSupportMessagesBeingDeleted={hintAboutSupportMessagesBeingDeleted}
          placeholder="Escreva uma resposta aqui..."
        />

        {(bodyAsPlainTextField.input.value || "").trim() && (
          <div
            css={{
              display: "grid",
              gridTemplateColumns: "1fr max-content",
              alignItems: "center",
            }}
          >
            <div>
              {bodyAsPlainTextField.meta.touched && bodyAsPlainTextField.meta.error && (
                <InlineErrorSpan>{bodyAsPlainTextField.meta.error}</InlineErrorSpan>
              )}
            </div>

            <div
              className="font-normal"
              css={{
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              <ButtonGroup stack="horizontally" spacing={20}>
                <LinkButton
                  disabled={submitting}
                  variant="black"
                  onClick={() => {
                    restartForm(form);

                    if (onCancel) {
                      onCancel();
                    }
                  }}
                >
                  Cancelar
                </LinkButton>

                <RaisedButton
                  disabled={!canSubmit}
                  type="submit"
                  variant={canSubmit ? "primary" : "disabled"}
                  height={36}
                >
                  {submitting ? "Aguarde..." : "Responder"}
                </RaisedButton>
              </ButtonGroup>
            </div>
          </div>
        )}

        {submitError && (
          <div
            css={{
              padding: 12,
              color: palette.stiletto,
              backgroundColor: palette.vanillaIce,
              borderRadius: 6,
            }}
          >
            <p
              css={{
                margin: 0,
                fontSize: 14,
                lineHeight: 1.33,
                letterSpacing: 1,
              }}
            >
              {submitError}
            </p>
          </div>
        )}
      </form>
    </div>
  );
}
